import type { ProductPackage } from '@/types/auth';

/** Hub jobs. Ads is hidden unless the package includes it. */
export const HUB_JOBS = [
  'setup',
  'invite',
  'prove',
  'foundation',
  'crawl',
  'ads',
] as const;

export type HubJob = (typeof HUB_JOBS)[number];

export const SCREENS = [
  'gate',
  'hub',
  'setup-info',
  'setup-area',
  'setup-website',
  'setup-review',
  'invite',
  'prove',
  'foundation',
  'crawl',
  'ads',
] as const;

export type OnboardingScreen = (typeof SCREENS)[number];

export const isOnboardingScreen = (v: unknown): v is OnboardingScreen =>
  typeof v === 'string' && (SCREENS as readonly string[]).includes(v);

export const JOB_COPY: Record<
  HubJob,
  { title: string; pending: string; done: string }
> = {
  setup: {
    title: 'Set up your business',
    pending: 'Name, website, and where you work',
    done: 'Business created',
  },
  invite: {
    title: 'Invite your team',
    pending: 'Or continue just you',
    done: 'Team set',
  },
  prove: {
    title: 'Prove the site',
    pending: 'Check the site is active',
    done: 'The site is active',
  },
  foundation: {
    title: 'Your business profile',
    pending: 'Profile, preferences, locations, calendar',
    done: 'Profile started',
  },
  crawl: {
    title: 'First site check',
    pending: 'The baseline we measure later against',
    done: 'Baseline check started',
  },
  ads: {
    title: 'Google Ads',
    pending: 'Map the account we advertise from',
    done: 'Ads mapped',
  },
};

export const SETUP_STEPS = [
  'setup-info',
  'setup-area',
  'setup-website',
  'setup-review',
] as const;

export type SetupStep = (typeof SETUP_STEPS)[number];

export const visibleJobs = (pkg: ProductPackage | null): HubJob[] =>
  HUB_JOBS.filter((job) => job !== 'ads' || pkg === 'ads' || pkg === 'both');

/** Jobs that must be done before Continue to dashboard. Invite is skippable (just me counts). */
export const requiredJobs = (pkg: ProductPackage | null): HubJob[] =>
  visibleJobs(pkg).filter((job) => job !== 'invite');

export const allRequiredDone = (
  pkg: ProductPackage | null,
  completed: readonly HubJob[],
): boolean => requiredJobs(pkg).every((job) => completed.includes(job));

export const jobForScreen = (screen: OnboardingScreen): HubJob | null => {
  if (screen.startsWith('setup-')) return 'setup';
  if (screen === 'invite') return 'invite';
  if (screen === 'prove') return 'prove';
  if (screen === 'foundation') return 'foundation';
  if (screen === 'crawl') return 'crawl';
  if (screen === 'ads') return 'ads';
  return null;
};

export const firstScreenForJob = (job: HubJob): OnboardingScreen => {
  switch (job) {
    case 'setup':
      return 'setup-info';
    case 'invite':
      return 'invite';
    case 'prove':
      return 'prove';
    case 'foundation':
      return 'foundation';
    case 'crawl':
      return 'crawl';
    case 'ads':
      return 'ads';
  }
};
